/**
 * AdminPageWrapper - Wrapper simple pour les pages admin qui n'utilisent pas AdminLayout
 * Ajoute le sidebar et le header admin avec le toggle thème
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminSidebar, AdminSidebarToggle } from './AdminSidebar';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Sun, Moon, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export function AdminPageWrapper({ children, title, subtitle, showBackButton = true }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { isLight, toggleTheme } = useAdminTheme();

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-200",
      isLight ? "bg-gray-100 admin-light-theme" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    )}>
      {/* Sidebar */}
      <AdminSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Header */}
        <header className={cn(
          "border-b backdrop-blur-sm sticky top-0 z-40",
          isLight ? "border-gray-200 bg-white/90" : "border-zinc-800 bg-zinc-900/50"
        )}>
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(true)} />
              {showBackButton && (
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate('/admin')}
                  className={isLight ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-white"}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              {title && (
                <div>
                  <h1 className={cn("text-xl font-bold", isLight ? "text-gray-900" : "text-white")}>
                    {title}
                  </h1>
                  {subtitle && (
                    <p className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                      {subtitle}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleTheme}
                className={cn(
                  "gap-2 transition-colors",
                  isLight 
                    ? "border-gray-300 bg-white hover:bg-gray-100 text-gray-700" 
                    : "border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                )}
                data-testid="theme-toggle"
              >
                {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="hidden sm:inline">{isLight ? 'Sombre' : 'Clair'}</span>
              </Button>
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminPageWrapper;
